import { K8sProvider, QueryOptions } from '@cast/shared-types';
import {
  KentNodeClass,
  ListAKSNodeClassesResponse,
  ListEC2NodeClassesResponse,
} from '@cast/types-kent';
import { makeRefetchOnlyFailed } from '@cast/utils';

import { useGetAKSNodeClassesQuery } from 'hooks/queries/kent/useGetAKSNodeClassesQuery';
import { useGetEC2NodeClassesQuery } from 'hooks/queries/kent/useGetEC2NodeClassesQuery';

type UseNodeClassesQueryArgs = {
  params: { clusterId: string; providerType?: K8sProvider };
  options?: QueryOptions<
    ListAKSNodeClassesResponse | ListEC2NodeClassesResponse
  >;
};

type Output = {
  nodeClasses: KentNodeClass[];
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
};

export const useNodeClassesQuery = ({
  params,
  options,
}: UseNodeClassesQueryArgs): Output => {
  const isAKSCluster = params.providerType === K8sProvider.AKS;

  // A cluster's node classes come from exactly one provider-specific endpoint (KENT-728 will unify).
  const {
    data: ec2Data,
    isLoading: isLoadingEC2,
    error: ec2Error,
    refetch: refetchEC2,
  } = useGetEC2NodeClassesQuery({
    params: { clusterId: params.clusterId },
    options: {
      ...options,
      enabled: options?.enabled !== false && !isAKSCluster,
    },
  });

  const {
    data: aksData,
    isLoading: isLoadingAKS,
    error: aksError,
    refetch: refetchAKS,
  } = useGetAKSNodeClassesQuery({
    params: { clusterId: params.clusterId },
    options: {
      ...options,
      enabled: options?.enabled !== false && isAKSCluster,
    },
  });

  const { error, refetch } = makeRefetchOnlyFailed(
    [ec2Error, refetchEC2],
    [aksError, refetchAKS]
  );

  return {
    nodeClasses: (isAKSCluster ? aksData : ec2Data)?.items ?? [],
    isLoading: isLoadingEC2 || isLoadingAKS,
    error,
    refetch,
  };
};
